import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import MenuCard from "../../component/MenuCard";
import EmptyHungryPage from "../../component/EmptyHungry/EmptyHungryPage";
import { clearCart, fetchMenuData } from "../../redux/action";
import { canOrderItem } from "../../Utills/menuAvailability";
import { createPublicTakeawayOrder, getPublicTakeawaySettings, rememberTrackedTakeawayOrder } from "./takeawayApi";
import { createPublicTakeawayPayment, verifyPublicTakeawayPayment } from "./takeawayApi";
import { openTakeawayRazorpay } from "./takeawayPayment";
import { useRazorpay } from "../../component/Payment/Razorpay";
import { toIndiaDateTimeInputValue } from "../../Utills/takeawayTime";
import { isValidTakeawayPhone } from "../../Utills/takeawayValidation";
import "../CartPage/cartPage.scss";
import "./takeaway.scss";

const TakeawayCheckout = () => {
  const { restaurant, id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const menu = useSelector((state) => state?.user?.menu);
  const cart = useSelector((state) => state?.user?.cart || {});
  const [settings, setSettings] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [checkoutStep, setCheckoutStep] = useState("cart");
  const [form, setForm] = useState({ customerName: "", customerPhone: "", pickupTime: "", specialInstructions: "", paymentMode: "PayAtPickup", fulfillmentType: 0 });
  const razorpayLoaded = useRazorpay();

  useEffect(() => {
    if (!menu?.categories?.length) dispatch(fetchMenuData(id));
    getPublicTakeawaySettings(id).then((data) => {
      setSettings(data);
      if (!data.allowPayAtPickup && data.allowOnlinePayment) setForm((current) => ({ ...current, paymentMode: "Online" }));
    }).catch((error) => toast.error(error.message));
  }, [dispatch, id, menu?.categories?.length]);

  const items = useMemo(() => Object.values(cart).map((line) => {
    const category = menu?.categories?.find((entry) => entry.categoryId === line.selectedTab);
    const original = category?.items?.find((entry) => entry.menuId === line.id);
    return original ? { ...original, ...line, menuId: original.menuId } : null;
  }).filter(Boolean), [cart, menu]);
  const subtotal = items.reduce((sum, item) => sum + Number(item.price) * Number(item.selected), 0);
  const itemCount = items.reduce((sum, item) => sum + Number(item.selected), 0);
  const isDineIn = form.fulfillmentType === 1;
  const packagingCharge = isDineIn ? 0 : Number(settings?.packagingCharge || 0);
  const total = subtotal + packagingCharge;
  const minimumOrderAmount = Number(settings?.minimumOrderAmount || 0);
  const minimumOrderMet = subtotal >= minimumOrderAmount;
  const minPickup = toIndiaDateTimeInputValue(new Date(Date.now() + 5 * 60000));
  const maxPickup = settings?.maximumScheduleDays
    ? toIndiaDateTimeInputValue(new Date(Date.now() + settings.maximumScheduleDays * 86400000)) : undefined;

  const submit = async (event) => {
    event.preventDefault();
    if (!isValidTakeawayPhone(form.customerPhone)) {
      setPhoneError("Enter a valid 10-digit Indian mobile number, optionally prefixed with +91.");
      return;
    }
    setPhoneError("");
    if (!settings?.isAcceptingOrders) return toast.error("The restaurant is not accepting takeaway orders.");
    if (!items.length || items.length !== Object.keys(cart).length || items.some((item) => !canOrderItem(item))) {
      dispatch(fetchMenuData(id));
      return toast.warn("Some items are unavailable. Please update your cart.");
    }
    console.log("Submitting takeaway order", { form, items });
    setSubmitting(true);
    try {
      const created = await createPublicTakeawayOrder(id, {
        fulfillmentType: form.fulfillmentType,
        customerName: form.customerName.trim() || null,
        customerPhone: form.customerPhone.trim() || null,
        pickupTime: isDineIn ? null : (form.pickupTime || null),
        specialInstructions: form.specialInstructions.trim() || null,
        paymentMode: form.paymentMode,
        items: items.map((item) => ({
          menuId: item.menuId,
          sizeOptionId: item.variant?.sizeOptionId || null,
          quantity: Number(item.selected),
          note: item.note || null,
          addonItemIds: (item.selectedAddons || []).map((addon) => addon.addonItemId),
        })),
      });
      rememberTrackedTakeawayOrder(id, created.order.takeawayOrderId, created.accessToken);
      dispatch(clearCart());
      const statusUrl = `/${restaurant}/${id}/takeaway/order/${created.order.takeawayOrderId}`;
      if (form.paymentMode !== "Online") {
        navigate(statusUrl, { state: { order: created.order } });
      } else {
        const gateway = await createPublicTakeawayPayment(id, created.order.takeawayOrderId, created.accessToken);
        let completed = false;
        openTakeawayRazorpay({
          gateway, restaurantName: restaurant, order: created.order,
          onSuccess: async (response) => {
            try {
              const paidOrder = await verifyPublicTakeawayPayment(id, created.order.takeawayOrderId, created.accessToken, {
                providerOrderId: response.razorpay_order_id,
                providerPaymentId: response.razorpay_payment_id,
                signature: response.razorpay_signature,
              });
              completed = true;
              navigate(statusUrl, { state: { order: paidOrder } });
            } catch (error) { toast.error(error.message); navigate(statusUrl, { state: { order: created.order } }); }
          },
          onDismiss: () => { if (!completed) navigate(statusUrl, { state: { order: created.order } }); },
          onFailure: () => toast.error("Payment failed. You can retry from order tracking."),
        });
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!items.length && menu?.categories?.length) return <div className="public-takeaway-state">
    <EmptyHungryPage showViewMenu={false} /><Link className="takeaway-link-button" to={`/${restaurant}/${id}/takeaway/menu`}>Browse menu</Link>
  </div>;

  const openStep = (step) => {
    setCheckoutStep(step);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return <div className="takeaway-checkout">
    <div className="takeaway-checkout-header">
      {checkoutStep === "cart"
        ? <Link to={`/${restaurant}/${id}/takeaway/menu`}>Back to menu</Link>
        : <button type="button" onClick={() => openStep("cart")}>Back to your order</button>}
      <h1>{checkoutStep === "cart" ? "Review your order" : "Order details"}</h1>
      <div className="takeaway-checkout-steps" aria-label="Checkout progress">
        <span className="active"><i>1</i>Order</span>
        <span className={checkoutStep === "details" ? "active" : ""}><i>2</i>Details</span>
      </div>
    </div>
    {checkoutStep === "cart" ? <div className="takeaway-checkout-grid takeaway-checkout-grid--cart">
      <section className="takeaway-checkout-items">
        <div className="takeaway-checkout-section-heading">
          <div><span>Your cart</span><h2>Order summary</h2></div>
          <strong>{itemCount} {itemCount === 1 ? "item" : "items"}</strong>
        </div>
        {items.map((item) => <MenuCard key={item.cartKey} menuId={item.menuId} src={item.imageUrl} title={item.foodName}
          description={item.description} price={item.price} tab={item.selectedTab} unit={item} isAvailable={item.isAvailable}
          note={item.note} showNote />)}
      </section>
      <aside className="takeaway-order-summary">
        {minimumOrderAmount > 0 && <small>Minimum order: ₹{minimumOrderAmount.toFixed(2)}</small>}
        <button type="button" disabled={!minimumOrderMet || !items.length} onClick={() => openStep("details")}>Continue to order details</button>
      </aside>
    </div> : <div className="takeaway-checkout-grid takeaway-checkout-grid--details">
      <form className="takeaway-customer-form" onSubmit={submit}>
        <div className="takeaway-form-heading"><span>Order type</span><h2>How would you like your order?</h2><p>Choose whether you will dine in or pick up the order.</p></div>
        <div className="takeaway-fulfillment-toggle" role="group" aria-label="Order type">
          <button type="button" aria-pressed={isDineIn} className={isDineIn ? "active" : ""} onClick={() => setForm({ ...form, fulfillmentType: 1, pickupTime: "" })}>Dine-in</button>
          <button type="button" aria-pressed={!isDineIn} className={!isDineIn ? "active" : ""} onClick={() => setForm({ ...form, fulfillmentType: 0 })}>Pick-up</button>
        </div>
        <div className="takeaway-contact-fields">
          <label>Name (optional)<input maxLength="120" autoComplete="name" value={form.customerName} onChange={(e) => setForm({ ...form, customerName: e.target.value })} /></label>
          <label>Phone (optional)<input type="tel" inputMode="tel" autoComplete="tel" maxLength="14" value={form.customerPhone}
            aria-invalid={Boolean(phoneError)} aria-describedby={phoneError ? "takeaway-phone-error" : undefined}
            onChange={(e) => { setForm({ ...form, customerPhone: e.target.value }); if (phoneError) setPhoneError(""); }} />
            {phoneError && <small id="takeaway-phone-error" className="takeaway-field-error" role="alert">{phoneError}</small>}
          </label>
        </div>
        {!isDineIn && settings?.allowScheduledPickup && <label>Schedule pickup (optional)<input type="datetime-local" min={minPickup} max={maxPickup} value={form.pickupTime} onChange={(e) => setForm({ ...form, pickupTime: e.target.value })} /></label>}
        <label>Order instructions (optional)<textarea maxLength="1000" rows="3" value={form.specialInstructions} onChange={(e) => setForm({ ...form, specialInstructions: e.target.value })} /></label>
        <label>Payment method<select value={form.paymentMode} onChange={(e) => setForm({ ...form, paymentMode: e.target.value })}>{settings?.allowPayAtPickup && <option value="PayAtPickup">{isDineIn ? "Pay at restaurant" : "Pay at pickup"}</option>}{settings?.allowOnlinePayment && <option value="Online">Pay online</option>}</select></label>
        <div className="takeaway-payment-box"><strong>{form.paymentMode === "Online" ? "Secure online payment" : isDineIn ? "Pay at restaurant" : "Pay at pickup"}</strong><span>{form.paymentMode === "Online" ? "The Razorpay checkout opens after your order is created." : isDineIn ? "Pay at the restaurant for your dine-in order." : "Pay when you collect the order."}</span></div>
        <div className="takeaway-price-lines"><span>Order type</span><b>{isDineIn ? "Dine-in" : "Pick-up"}</b><span>Subtotal</span><b>₹{subtotal.toFixed(2)}</b><span>Packaging</span><b>₹{packagingCharge.toFixed(2)}</b><span>Order amount</span><b>₹{total.toFixed(2)}</b></div>
        <small>Applicable tax and configured service charge are calculated securely before online payment or final billing.</small>
        {settings?.minimumOrderAmount > 0 && <small>Minimum order: ₹{Number(settings.minimumOrderAmount).toFixed(2)}</small>}
        <button disabled={submitting || !minimumOrderMet || (form.paymentMode === "Online" && !razorpayLoaded)}>{submitting ? "Placing order..." : isDineIn ? "Place dine-in order" : "Place pickup order"}</button>
      </form>
    </div>}
  </div>;
};

export default TakeawayCheckout;
